import React, { lazy, useLayoutEffect } from 'react';
import { Switch, Route } from 'react-router-dom';
import ScrollToTop from 'components/Global/ScrollToTop/ScrollToTop';
import { IPage } from 'interfaces/pages';
import Metadata from 'components/Global/Metadata/Metadata';
import { useMetaData } from 'hooks/useMetaData';
import ZipGateModal from 'components/Modal/ZipGateModal/ZipGateModal';
import { createBrowserHistory } from 'history';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';

const SentryRoute = Sentry.withSentryRouting(Route);

const history = createBrowserHistory();

const ReserveImport = lazy(
  () => import(/* webpackChunkName: "reserve-online" */ 'pages/Reserve/Reserve')
);

const ConfirmationImport = lazy(
  () =>
    import(
      /* webpackChunkName: "payment-confirmation" */ 'pages/PaymentConfirmation/PaymentConfirmation'
    )
);

const NonReactConciergeAiEmbedChatImport = lazy(
  () =>
    import(
      /* webpackChunkName: "non-react-concierge-ai-embed" */ 'pages/Concierge/ConciergeAiEmbedChat'
    )
);

// Sentry initializer, would be integrated with react router
if (window.Acr?.sentryConfig?.dsn) {
  Sentry.init({
    ...window.Acr.sentryConfig,
    beforeSendTransaction(event) {
      event.transaction = event.transaction?.toLowerCase();
      return event;
    },
    integrations: [
      new BrowserTracing({
        routingInstrumentation: Sentry.reactRouterV5Instrumentation(history),
        tracePropagationTargets: ['localhost', /^\//],
      }),
    ],
  });
}

const HondaPageRouting = () => {
  const { data } = useMetaData();

  const optionQuery = document.querySelectorAll(
    "[isconciergeaiembedchat='true']"
  );

  useLayoutEffect(() => {
    document.body.classList.add('acr-focus-disabled');
  }, []);

  const getComponent = (page: IPage) => {
    switch (page.type) {
      case 'reserve':
        return <ReserveImport />;
      case 'confirmation':
        return <ConfirmationImport />;
      default:
        return;
    }
  };

  if (data && optionQuery.length === 0) {
    return (
      <>
        <ScrollToTop />
        <ZipGateModal labels={data.pageContent.labels} />
        <Switch>
          {data.pageMetadata.pages.map((page) => {
            return page.type ? (
              <SentryRoute
                exact
                path={`${page.path.replace(/\/\s*$/, '')}${
                  page.type === 'buildandprice' ? '/:vehicleModelSeries' : ''
                }`}
                key={`route${page.id}`}
              >
                <Metadata
                  metadata={page.metadata}
                  canonicalUrls={data.pageMetadata.canonicalUrls}
                />
                {getComponent(page)}
              </SentryRoute>
            ) : null;
          })}
          <SentryRoute exact path="/">
            <h1>NOT FOUND</h1>
          </SentryRoute>
          <SentryRoute path="*">
            <h1>NOT FOUND</h1>
          </SentryRoute>
        </Switch>
      </>
    );
  } else if (optionQuery.length !== 0) {
    const conciergeAiEmbedChat = document.querySelector(
      "[isconciergeaiembedchat='true']"
    );
    return (
      <>{conciergeAiEmbedChat && <NonReactConciergeAiEmbedChatImport />}</>
    );
  }
  return <></>;
};

export default HondaPageRouting;
